import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { 
  Mail, Search, Download, Filter, Home, MessageSquare,
  LogOut, Eye, Trash2, Phone, User, Clock, Plus, Calendar
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
  DialogFooter
} from '../../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_4f8d1127-7831-43ae-8d3d-944f658ba2e9/artifacts/6bknvtgo_logo.png';

export const AdminContacts = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [newInteraction, setNewInteraction] = useState({
    topic: '',
    discussion_summary: '',
    next_follow_up_date: ''
  });

  useEffect(() => {
    fetchContacts();
  }, [search, statusFilter]);

  const fetchContacts = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await axios.get(`${API}/contacts?${params}`);
      setContacts(response.data);
    } catch (error) {
      toast.error('Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  };

  const fetchInteractions = async (contactId) => {
    try {
      const response = await axios.get(`${API}/interactions/${contactId}`);
      setInteractions(response.data);
    } catch (error) {
      toast.error('Failed to fetch interactions');
    }
  };

  const handleViewContact = async (contact) => {
    setSelectedContact(contact);
    await fetchInteractions(contact.id);
  };

  const handleAddInteraction = async () => {
    if (!selectedContact) return;
    
    try {
      await axios.post(`${API}/interactions`, {
        contact_id: selectedContact.id,
        ...newInteraction
      });
      toast.success('Interaction added');
      setNewInteraction({ topic: '', discussion_summary: '', next_follow_up_date: '' });
      fetchInteractions(selectedContact.id);
      fetchContacts();
    } catch (error) {
      toast.error('Failed to add interaction');
    }
  };

  const handleUpdateStatus = async (contactId, status) => {
    try {
      await axios.put(`${API}/contacts/${contactId}`, { status });
      toast.success('Status updated');
      fetchContacts();
      if (selectedContact?.id === contactId) {
        setSelectedContact({ ...selectedContact, status });
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    
    try {
      await axios.delete(`${API}/contacts/${id}`);
      toast.success('Contact deleted');
      fetchContacts();
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  const handleExport = () => {
    window.open(`${API}/contacts/export/csv`, '_blank');
  };

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const newCount = contacts.filter(c => c.status === 'new').length;
  const contactedCount = contacts.filter(c => c.status === 'contacted').length;

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-900" data-testid="admin-contacts">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 w-64 h-full bg-slate-900 text-white z-40">
        <div className="p-6 border-b border-slate-800">
          <img src={LOGO_URL} alt="RENVITEK" className="h-10" />
          <p className="text-xs text-slate-400 mt-2">Admin Panel</p>
        </div>
        
        <nav className="p-4 space-y-2">
          <Link
            to="/admin/dashboard"
            className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-slate-800 text-slate-300 transition-colors"
          >
            <Home size={20} />
            Dashboard
          </Link>
          <Link
            to="/admin/subscribers"
            className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-slate-800 text-slate-300 transition-colors"
          >
            <Mail size={20} />
            Subscribers
          </Link>
          <Link
            to="/admin/contacts"
            className="flex items-center gap-3 px-4 py-3 rounded-lg bg-emerald-600 text-white"
          >
            <MessageSquare size={20} />
            Contacts
          </Link>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center">
              {user?.username?.charAt(0).toUpperCase() || 'A'}
            </div>
            <div>
              <p className="text-sm font-medium">{user?.username || 'Admin'}</p>
              <p className="text-xs text-slate-400">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
              Contacts
            </h1>
            <p className="text-slate-600 dark:text-slate-400">
              Manage contact requests (CRM)
            </p>
          </div>
          <Button
            onClick={handleExport}
            className="bg-emerald-600 hover:bg-emerald-700 flex items-center gap-2"
            data-testid="export-contacts-csv-btn"
          >
            <Download size={18} />
            Export CSV
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-6 mb-8">
          <div className="bg-white dark:bg-slate-800 rounded-lg p-6 shadow-sm">
            <p className="text-sm text-slate-500">Total Contacts</p>
            <p className="text-3xl font-bold text-slate-900 dark:text-white">{contacts.length}</p>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-lg p-6 shadow-sm">
            <p className="text-sm text-slate-500">New Requests</p>
            <p className="text-3xl font-bold text-blue-500">{newCount}</p>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-lg p-6 shadow-sm">
            <p className="text-sm text-slate-500">Contacted</p>
            <p className="text-3xl font-bold text-emerald-500">{contactedCount}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-slate-800 rounded-lg p-4 mb-6 flex gap-4 items-center">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
            <Input
              type="text"
              placeholder="Search by name, email, phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="contacts-search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-slate-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-slate-200 dark:border-slate-700 rounded-lg px-4 py-2 bg-white dark:bg-slate-800"
              data-testid="contacts-status-filter"
            >
              <option value="">All Status</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 dark:bg-slate-700">
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Contact</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="loading-spinner mx-auto" />
                  </TableCell>
                </TableRow>
              ) : contacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    No contacts found
                  </TableCell>
                </TableRow>
              ) : (
                contacts.map((contact) => (
                  <TableRow key={contact.id} data-testid={`contact-row-${contact.id}`}>
                    <TableCell className="font-medium">{contact.name}</TableCell>
                    <TableCell>{contact.email}</TableCell>
                    <TableCell>{contact.phone}</TableCell>
                    <TableCell className="max-w-xs truncate">{contact.subject}</TableCell>
                    <TableCell>
                      <span className={`status-badge ${
                        contact.status === 'new' ? 'status-new' : 
                        contact.status === 'contacted' ? 'status-contacted' : 
                        'status-closed'
                      }`}>
                        {contact.status}
                      </span>
                    </TableCell>
                    <TableCell>
                      {contact.last_contact_date 
                        ? new Date(contact.last_contact_date).toLocaleDateString()
                        : <span className="text-slate-400">—</span>
                      }
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleViewContact(contact)}
                              className="text-blue-600 hover:bg-blue-50"
                              data-testid={`view-contact-btn-${contact.id}`}
                            >
                              <Eye size={16} />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle>Contact Details</DialogTitle>
                            </DialogHeader>
                            
                            {selectedContact && (
                              <div className="space-y-6">
                                {/* Contact Profile */}
                                <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-lg">
                                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                                    <User className="text-emerald-500" />
                                    Customer Profile
                                  </h3>
                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <p className="text-sm text-slate-500">Name</p>
                                      <p className="font-medium">{selectedContact.name}</p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-slate-500">Email</p>
                                      <p className="font-medium">{selectedContact.email}</p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-slate-500">Phone</p>
                                      <p className="font-medium">{selectedContact.phone}</p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-slate-500">Status</p>
                                      <select
                                        value={selectedContact.status}
                                        onChange={(e) => handleUpdateStatus(selectedContact.id, e.target.value)}
                                        className="border rounded px-2 py-1 text-sm"
                                      >
                                        <option value="new">New</option>
                                        <option value="contacted">Contacted</option>
                                        <option value="closed">Closed</option>
                                      </select>
                                    </div>
                                    <div className="col-span-2">
                                      <p className="text-sm text-slate-500">Subject</p>
                                      <p className="font-medium">{selectedContact.subject}</p>
                                    </div>
                                    <div className="col-span-2">
                                      <p className="text-sm text-slate-500">Message</p>
                                      <p className="text-slate-700 dark:text-slate-300">{selectedContact.message}</p>
                                    </div>
                                  </div>
                                </div>

                                {/* Interaction History */}
                                <div>
                                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                                    <Clock className="text-emerald-500" />
                                    Interaction History
                                  </h3>
                                  
                                  {interactions.length === 0 ? (
                                    <p className="text-slate-500 text-center py-4">No interactions yet</p>
                                  ) : (
                                    <div className="space-y-3">
                                      {interactions.map((interaction) => (
                                        <div key={interaction.id} className="bg-slate-50 dark:bg-slate-800 p-4 rounded-lg">
                                          <div className="flex justify-between items-start mb-2">
                                            <p className="font-medium text-emerald-600">{interaction.topic}</p>
                                            <p className="text-xs text-slate-500">
                                              {new Date(interaction.date).toLocaleString()}
                                            </p>
                                          </div>
                                          <p className="text-sm text-slate-600 dark:text-slate-300">
                                            {interaction.discussion_summary}
                                          </p>
                                          {interaction.next_follow_up_date && (
                                            <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                                              <Calendar size={12} />
                                              Follow-up: {new Date(interaction.next_follow_up_date).toLocaleDateString()}
                                            </p>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                {/* Add Interaction */}
                                <div className="bg-emerald-50 dark:bg-emerald-900/20 p-6 rounded-lg">
                                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                                    <Plus className="text-emerald-500" />
                                    Add Interaction
                                  </h3>
                                  <div className="space-y-4">
                                    <Input
                                      placeholder="Topic discussed"
                                      value={newInteraction.topic}
                                      onChange={(e) => setNewInteraction({ ...newInteraction, topic: e.target.value })}
                                      data-testid="interaction-topic-input"
                                    />
                                    <Textarea
                                      placeholder="Summary of discussion..."
                                      value={newInteraction.discussion_summary}
                                      onChange={(e) => setNewInteraction({ ...newInteraction, discussion_summary: e.target.value })}
                                      rows={3}
                                      data-testid="interaction-summary-input"
                                    />
                                    <div>
                                      <label className="text-sm text-slate-500 block mb-1">Next Follow-up Date (optional)</label>
                                      <Input
                                        type="date"
                                        value={newInteraction.next_follow_up_date}
                                        onChange={(e) => setNewInteraction({ ...newInteraction, next_follow_up_date: e.target.value })}
                                        data-testid="interaction-followup-input"
                                      />
                                    </div>
                                    <Button 
                                      onClick={handleAddInteraction}
                                      className="bg-emerald-600 hover:bg-emerald-700"
                                      data-testid="add-interaction-btn"
                                    >
                                      Add Interaction
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </DialogContent>
                        </Dialog>
                        
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(contact.id)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </main>
    </div>
  );
};

export default AdminContacts;
